import auth from '~/auth';

import { ToolCallInfo } from '~/components/AiChat/AiChatContext';
import type { ChatThreadMode } from '~/components/AiChat/AiChatContext';

import ApiError from '~/types/ApiError';

import { json_headers, request } from './common';

export interface ChatRequestItem {
  role: 'user';
  content: string;
  kind: 'context' | 'user';
}

export interface ChatRequestContext {
  conversationRef: string | null;
  agentId: string | null;
  agentName?: string | null;
  agentEmail?: string | null;
  agentRoles?: Array<string>;
  customerId?: string | null;
  customerName?: string | null;
  orderId?: string | null;
  department?: string | null;
  mode?: ChatThreadMode;
  page?: string | null;
  hasPriorFraudDecision?: boolean;
}

export interface CreateConversationResponse {
  conversationId: string;
}

export interface SendConversationItemsRequest {
  conversationId: string;
  context: ChatRequestContext;
  items: Array<ChatRequestItem>;
  model?: string;
}

export interface ChatResponse {
  content: string;
  toolCalls: Array<ToolCallInfo>;
  responseId: string | null;
}

export type AdminChatStreamStatus =
  | { phase: 'reasoning' }
  | {
      phase: 'tool_calling';
      tool?: string;
      arguments?: Record<string, unknown>;
    };

export interface SendConversationItemsStreamHandlers {
  onHeartbeat?: () => void;
  onStatus?: (status: AdminChatStreamStatus) => void;
  onTokenBatch?: (text: string) => void;
  onToolCall?: (toolCall: ToolCallInfo) => void;
}

export interface SendConversationItemsStreamOptions {
  signal?: AbortSignal;
}

interface ResponsesApiOutput {
  type: string;
  role?: string;
  content?: Array<{ type: string; text: string }>;
}

interface ResponsesApiResult {
  id: string;
  output: Array<ResponsesApiOutput>;
  toolCalls?: Array<ToolCallInfo>;
}

const DEFAULT_ADMIN_AI_API_PATH = '/api/admin-ai';

function getAdminAiApiUrl(path: `/${string}`): string {
  const baseUrl = window.configs.ADMIN_AI_API_BASE_URL || `${window.configs.API_HOST}${DEFAULT_ADMIN_AI_API_PATH}`;
  return `${baseUrl}${path}`;
}

function waitForNextPaint(): Promise<void> {
  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    return new Promise((resolve) => {
      const timer = setTimeout(resolve, 50);
      window.requestAnimationFrame(() => {
        clearTimeout(timer);
        resolve();
      });
    });
  }

  return new Promise((resolve) => {
    setTimeout(resolve, 0);
  });
}

export async function createConversation(): Promise<CreateConversationResponse> {
  const result = await request(getAdminAiApiUrl('/conversations'), {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({}),
  });

  return {
    conversationId: result?.conversationId,
  };
}

export async function sendConversationItems(payload: SendConversationItemsRequest): Promise<ChatResponse> {
  const result: ResponsesApiResult = await request(getAdminAiApiUrl('/responses'), {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(payload),
  });

  const messageOutput = result.output?.find((o) => o.type === 'message' && o.role === 'assistant');
  const textContent = messageOutput?.content?.find((c) => c.type === 'output_text');

  return {
    content: textContent?.text || 'No response received.',
    toolCalls: result.toolCalls || [],
    responseId: result.id || null,
  };
}

export async function sendConversationItemsStream(
  payload: SendConversationItemsRequest,
  handlers: SendConversationItemsStreamHandlers = {},
  options: SendConversationItemsStreamOptions = {},
): Promise<ChatResponse> {
  const token = await auth.getAccessToken();

  if (!token) {
    throw new Error('You are not logged in.');
  }

  const response = await fetch(getAdminAiApiUrl('/responses/stream'), {
    method: 'POST',
    headers: {
      ...json_headers,
      Authorization: `Bearer ${token}`,
    },
    credentials: 'include',
    signal: options.signal,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw await getResponseError(response);
  }

  if (!response.body) {
    throw new Error('Streaming response was not available.');
  }

  return readAdminChatStream(response.body, handlers);
}

export async function transcribeAudio(audioBlob: Blob): Promise<string> {
  const formData = new FormData();
  formData.append('file', audioBlob, 'recording.webm');

  const result = await request(getAdminAiApiUrl('/audio/transcriptions'), {
    method: 'POST',
    body: formData,
  });

  return result?.text || '';
}

async function readAdminChatStream(
  body: ReadableStream<Uint8Array>,
  handlers: SendConversationItemsStreamHandlers,
): Promise<ChatResponse> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let content = '';
  let responseId: string | null = null;
  let toolCalls: Array<ToolCallInfo> = [];

  try {
    let result = await reader.read();
    while (!result.done) {
      const { value } = result;

      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split(/\r?\n\r?\n/);
      buffer = frames.pop() || '';

      for (const frame of frames) {
        const result = handleStreamFrame(frame, handlers, {
          content,
          responseId,
          toolCalls,
        });

        if (result.type === 'error') {
          throw new Error(result.message);
        }

        content = result.content;
        responseId = result.responseId;
        toolCalls = result.toolCalls;

        if (result.type === 'message_end') {
          return {
            content: content || 'No response received.',
            responseId,
            toolCalls,
          };
        }

        if (result.type === 'token_batch') {
          await waitForNextPaint();
        }
      }

      result = await reader.read();
    }

    if (buffer.trim()) {
      const result = handleStreamFrame(buffer, handlers, {
        content,
        responseId,
        toolCalls,
      });

      if (result.type === 'error') {
        throw new Error(result.message);
      }

      content = result.content;
      responseId = result.responseId;
      toolCalls = result.toolCalls;

      if (result.type === 'message_end') {
        return {
          content: content || 'No response received.',
          responseId,
          toolCalls,
        };
      }

      if (result.type === 'token_batch') {
        await waitForNextPaint();
      }
    }

    throw new Error('Streaming response ended before completion.');
  } finally {
    reader.releaseLock();
  }
}

function handleStreamFrame(
  frame: string,
  handlers: SendConversationItemsStreamHandlers,
  current: ChatResponse,
):
  | (ChatResponse & { type: 'continue' | 'message_end' | 'token_batch' })
  | (ChatResponse & { type: 'error'; message: string }) {
  const parsedFrame = parseSseFrame(frame);
  const data = parseStreamData(parsedFrame.data);

  if (parsedFrame.event === 'heartbeat') {
    handlers.onHeartbeat?.();
    return { ...current, type: 'continue' };
  }

  if (parsedFrame.event === 'status') {
    if (typeof data?.phase === 'string') {
      handlers.onStatus?.(data as AdminChatStreamStatus);
    }
    return { ...current, type: 'continue' };
  }

  if (parsedFrame.event === 'token_batch') {
    const text = typeof data?.text === 'string' ? data.text : '';
    if (text) {
      handlers.onTokenBatch?.(text);
    }
    return { ...current, content: `${current.content}${text}`, type: 'token_batch' };
  }

  if (parsedFrame.event === 'tool_call') {
    const toolCall = data?.toolCall as ToolCallInfo | undefined;
    if (toolCall?.name) {
      handlers.onToolCall?.(toolCall);
      return {
        ...current,
        toolCalls: [...current.toolCalls, toolCall],
        type: 'continue',
      };
    }
    return { ...current, type: 'continue' };
  }

  if (parsedFrame.event === 'message_end') {
    return {
      content: typeof data?.content === 'string' ? data.content : current.content,
      responseId: typeof data?.responseId === 'string' ? data.responseId : current.responseId,
      toolCalls: Array.isArray(data?.toolCalls) ? data.toolCalls : current.toolCalls,
      type: 'message_end',
    };
  }

  if (parsedFrame.event === 'error') {
    return {
      ...current,
      type: 'error',
      message: typeof data?.message === 'string' ? data.message : 'Streaming response failed.',
    };
  }

  return { ...current, type: 'continue' };
}

function parseSseFrame(frame: string): { event: string; data: string } {
  const lines = frame.split(/\r?\n/);
  const event =
    lines
      .find((line) => line.startsWith('event:'))
      ?.slice(6)
      .trim() || 'message';
  const data = lines
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trimStart())
    .join('\n');

  return { event, data };
}

function parseStreamData(data: string): Record<string, unknown> {
  if (!data) {
    return {};
  }

  try {
    return JSON.parse(data);
  } catch {
    return {};
  }
}

async function getResponseError(response: Response): Promise<ApiError> {
  const fallback = `Network error received (status code ${response.status}): Garbled response`;

  try {
    const body = await response.json();
    const bodyError = body?.error;
    const rawMessage =
      body?.message ||
      (typeof bodyError === 'string' ? bodyError : bodyError?.message) ||
      body?.body?.message ||
      'Unknown error';
    const message = `Network error received (status code ${response.status}): ${rawMessage}`;

    return new ApiError({
      message,
      name: 'NetworkError',
      status: response.status,
      errors: body?.errors || body?.issues || body?.body?.errors,
    });
  } catch {
    return new ApiError({
      message: fallback,
      name: 'NetworkError',
      status: response.status,
    });
  }
}
